import type { MouseEventHandler } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { ShortenedTextAPI } from '@elfsight/widget-ui';
import { ShortenedText } from '@elfsight/widget-ui';
import { getLineHeight, useResizeObserver } from '@elfsight/widget-utils';
import { useTranslation } from 'react-i18next';
import { useLayout } from '../../layout';
import { usePost } from '../../post';
import { usePostAction, usePostSettings } from '../../post/utils';
import { useSocialFeedSettings } from '../../utils';
import { stableRound, useLinesCount } from '../utils';

type TextAlignType = 'left' | 'center';

interface TextProps {
  text: string;
  disableControl?: boolean;
  align?: TextAlignType;
  lines?: number;
}

export function Text({
  text,
  disableControl = false,
  align = 'left',
  lines = 3,
  ...forwardingProps
}: TextProps) {
  const { t } = useTranslation();
  const settings = useSocialFeedSettings();
  const {
    textColor,
    linksColor,
    showText,
    heightNormalization,
    textLength: textDisplayMode,
    textStyle: { fontSize: textSize }
  } = usePostSettings();
  const { rerender, carouselVisibleIndexes } = useLayout();
  const handleAction = usePostAction();
  const {
    index,
    hasMedia,
    hasTextLink,
    hasRepost,
    horizontal,
    isReadMoreRendered,
    setIsReadMoreRendered,
    rect: { height: postHeight }
  } = usePost();
  const shortenedTextRef = useRef<ShortenedTextAPI | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);

  const { shortenText, controlRef } = shortenedTextRef.current ?? {};

  const [{ height: containerHeight }, revalidateContainerHeight] =
    useResizeObserver(containerRef.current);
  const prevPostHeight = useRef(postHeight);

  const lineSize = parseInt(
    getLineHeight({ fontSize: textSize }) as string,
    10
  );

  const { current: readMoreElement } = controlRef ?? {};
  const lineDecrement = stableRound(
    (readMoreElement?.offsetHeight ?? 0) / lineSize
  );

  const linesCount = useLinesCount({
    rawLinesCount: lines,
    lineSize,
    containerHeight,
    lineDecrement
  });

  useLayoutEffect(() => {
    const maxTolerance = 10;
    const isDeltaHeightInsufficient =
      Math.abs(prevPostHeight.current - postHeight) <= maxTolerance;

    if (isDeltaHeightInsufficient) {
      return;
    }

    revalidateContainerHeight();
    prevPostHeight.current = postHeight;
  }, [revalidateContainerHeight, settings, postHeight]);

  const readMoreSize = !(hasMedia || hasTextLink || hasRepost)
    ? textSize - 4
    : textSize;

  const handleControlClick: MouseEventHandler<HTMLElement> = (e) => {
    if (rerender) {
      requestAnimationFrame(() => {
        rerender();
      });
    }

    if (heightNormalization || horizontal) {
      handleAction?.(e);

      return true;
    }

    return undefined;
  };

  useEffect(() => {
    rerender?.();
    // Ререндер необходимо запустить только один раз
  }, []);

  // Сворачивание отзыва при изменении настроек
  useEffect(() => {
    shortenText?.();
  }, [shortenText, heightNormalization, textDisplayMode, showText]);

  // Сворачивание текста отзыва, если он вышел за пределы видимости карусели
  useEffect(() => {
    if (!carouselVisibleIndexes.includes(index)) {
      shortenText?.();
    }
  }, [shortenText, index, carouselVisibleIndexes]);

  return (
    <Container
      {...forwardingProps}
      ref={containerRef}
      _isReadMoreRendered={isReadMoreRendered}
      _isCardHorizontal={horizontal}
      _textAlign={align}
      _linksColor={linksColor}
    >
      <ShortenedText
        ref={shortenedTextRef}
        text={text}
        textLineHeight={`${lineSize}px`}
        textDisplayMode={textDisplayMode}
        align={align}
        textMaxLines={linesCount}
        textSize={textSize}
        textColor={textColor}
        color={textColor}
        controlOpenText={t('Read more')}
        controlCloseText={t('Hide')}
        displayControl={disableControl ? false : undefined}
        controlSize={readMoreSize}
        controlColor={getColorWithAlpha(textColor, 0.7)}
        onIsClampedChange={setIsReadMoreRendered}
        onShortenerClick={handleControlClick}
      />
    </Container>
  );
}

const PreviewLinkContainer = styled.div`
  width: 100%;
  margin-top: 12px;
`;

const Container = styled.div<{
  _linksColor: string;
  _textAlign: TextAlignType;
  _isReadMoreRendered: boolean;
  _isCardHorizontal: boolean;
}>`
  width: 100%;
  height: ${({ _isCardHorizontal, _isReadMoreRendered }) =>
    _isCardHorizontal && !_isReadMoreRendered ? 'auto' : '100%'};
  text-align: ${({ _textAlign }) => _textAlign};

  ${({ _isCardHorizontal }) =>
    _isCardHorizontal &&
    css`
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    `};

  & :not(${PreviewLinkContainer}) p {
    margin: 0;
  }

  & :not(${PreviewLinkContainer}) a {
    text-decoration: none;
    color: ${({ _linksColor }) => _linksColor};

    &:hover,
    &:focus {
      text-decoration: underline;
    }
  }
`;
